'use client';

import { c as _c } from "react/compiler-runtime";
import { QuickFilters, type QuickFilterPreset, type QuickFiltersLayout, type QuickFiltersSelectionMode, type QuickFilterValue } from '@/components/filters/QuickFilters';
import type { InspirationFilterValue } from '@/components/demand/InspirationFilters';
import { useInspirationFeedQuickFilters } from '@/lib/hooks/useInspirationFeedQuickFilters';
interface InspirationFeedQuickFiltersProps {
  /** Current inspiration-feed filter selection. */
  value: InspirationFilterValue;
  /**
   * Explicit preset list. When provided, these are used verbatim and the
   * metadata-driven presets are NOT loaded. When omitted, presets come from
   * {@link useInspirationFeedQuickFilters} (metadata with built-in defaults).
   */
  presets?: readonly QuickFilterPreset[];
  /** Called with the next selection when a preset is toggled. */
  onChange: (value: InspirationFilterValue) => void;
  /** Optional leading label shown before the pills. Defaults to "Quick picks". */
  label?: string;
  /** Display style. Defaults to `auto` (dropdown on mobile, pills otherwise). */
  layout?: QuickFiltersLayout;
  /** Selection behaviour. Defaults to `quick` (one-shot shortcuts, no state). */
  selectionMode?: QuickFiltersSelectionMode;
  className?: string;
}

/**
 * Inspiration Feed quick-filter pills. Domain wrapper around the generic
 * {@link QuickFilters}: it pulls the feed's presets via
 * {@link useInspirationFeedQuickFilters} (metadata-driven, with defaults) and
 * bridges the typed {@link InspirationFilterValue} to the generic
 * `Record<string, string[]>` value QuickFilters operates on.
 *
 * Renders nothing until presets are available (the hook is non-optimistic, so
 * the pills appear once loaded rather than flashing defaults).
 */
export function InspirationFeedQuickFilters(t0) {
  const $ = _c(11);
  const {
    value,
    onChange,
    presets: presetsOverride,
    label: t1,
    layout,
    selectionMode,
    className
  } = t0;
  const label = t1 === undefined ? "Quick filter" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      optimistic: false
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    presets: metadataPresets
  } = useInspirationFeedQuickFilters(t2);
  const presets = presetsOverride ?? metadataPresets;
  if (presets.length === 0) {
    return null;
  }
  const t3 = value as unknown as QuickFilterValue;
  let t4;
  if ($[1] !== onChange) {
    t4 = next => onChange(next as unknown as InspirationFilterValue);
    $[1] = onChange;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== className || $[4] !== label || $[5] !== layout || $[6] !== presets || $[7] !== selectionMode || $[8] !== t3 || $[9] !== t4) {
    t5 = <QuickFilters className={className} label={label} layout={layout} selectionMode={selectionMode} presets={presets} value={t3} onChange={t4} />;
    $[3] = className;
    $[4] = label;
    $[5] = layout;
    $[6] = presets;
    $[7] = selectionMode;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}